import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'

import Database from '../../types/Database'
import { generateColorFromString } from '../../utils/FormatHelpers'

interface OfficeFilterProps {
  offices: Database.Office[]
  selectedOfficeIds: string[]
  onFilterChange: (officeIds: string[]) => void
}

/**
 * OfficeFilter - Interactive office filter for calendar bookings
 *
 * Allows users to select one or multiple offices from a dropdown to filter
 * calendar bookings. Selected offices are displayed as removable chips below the selector.
 */
export default function OfficeFilter({
  offices,
  selectedOfficeIds,
  onFilterChange
}: OfficeFilterProps) {
  // Initialize with all offices selected if none are selected
  // This effect runs before the early return check
  React.useEffect(() => {
    if (offices.length > 1 && selectedOfficeIds.length === 0) {
      const allOfficeIds = offices.map(o => o.oid ?? '').filter(Boolean)
      onFilterChange(allOfficeIds)
    }
  }, [offices, selectedOfficeIds.length, onFilterChange])

  // Don't show filter if only one office or none
  if (offices.length <= 1) {
    return null
  }

  // Derive selected offices from IDs
  const selectedOffices = offices.filter(o =>
    selectedOfficeIds.includes(o.oid ?? '')
  )

  // Handle office selection from Autocomplete
  const handleOfficeChange = (
    _event: React.SyntheticEvent,
    newValue: Database.Office[]
  ) => {
    const newIds = newValue.map(o => o.oid ?? '').filter(Boolean)
    onFilterChange(newIds)
  }

  // Handle individual chip removal
  const handleRemoveOffice = (officeId: string) => {
    const newIds = selectedOfficeIds.filter(id => id !== officeId)
    onFilterChange(newIds)
  }

  // Handle select all
  const handleSelectAll = () => {
    const allOfficeIds = offices.map(o => o.oid ?? '').filter(Boolean)
    onFilterChange(allOfficeIds)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        backgroundColor: theme =>
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.03)',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography
        variant='caption'
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: 'text.primary'
        }}
      >
        Filter by office
      </Typography>

      <Autocomplete
        multiple
        options={offices}
        value={selectedOffices}
        onChange={handleOfficeChange}
        getOptionLabel={office => office.name ?? ''}
        isOptionEqualToValue={(option, value) => option.oid === value.oid}
        renderTags={() => null}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={
              selectedOffices.length > 0
                ? `${selectedOffices.length} office${
                    selectedOffices.length > 1 ? 's' : ''
                  } selected`
                : 'Select offices...'
            }
            size='small'
          />
        )}
        size='small'
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper'
          }
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center'
        }}
      >
        {selectedOffices.map(office => {
          const officeColor = generateColorFromString(office.name ?? '')
          return (
            <Chip
              key={office.oid}
              label={office.name}
              onDelete={() => handleRemoveOffice(office.oid ?? '')}
              size='small'
              sx={{
                backgroundColor: officeColor,
                color: theme =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.87)'
                    : 'rgba(255, 255, 255, 0.95)',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: theme =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.6)'
                      : 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: theme =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(0, 0, 0, 0.87)'
                        : 'rgba(255, 255, 255, 0.95)'
                  }
                }
              }}
            />
          )
        })}
        {selectedOfficeIds.length > 0 &&
          selectedOfficeIds.length < offices.length && (
            <Button
              size='small'
              onClick={handleSelectAll}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                minWidth: 'auto',
                px: 1
              }}
            >
              Select all
            </Button>
          )}
      </Box>
    </Box>
  )
}
